/** Fuel oil application letter — data helpers. */

export const FUEL_TYPE_VALUES = ['petrol', 'octane', 'diesel'];

export const INITIAL_FUEL_CARD_FORM = {
    applicantName: '',
    fatherOrProprietor: '',
    nidOrLicense: '',
    mobile: '',
    villageRoad: '',
    union: '',
    postOffice: '',
    thanaUpazila: '',
    district: '',
    pumpName: '',
    machineName: '',
    fuelType: 'diesel',
    fuelLiters: '',
    purpose: '',
};

export function displayField(value) {
    const v = String(value ?? '').trim();
    return v || '';
}

function toAsciiDigits(str) {
    return str
        .replace(/[\u09E6-\u09EF]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
        .replace(/[\u0660-\u0669]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
}

export function normalizeDigits(value, maxLength) {
    const digits = String(value ?? '').replace(/[^\d\u09E6-\u09EF\u0660-\u0669]/g, '');
    return maxLength ? digits.slice(0, maxLength) : digits;
}

export function normalizeBdMobile(value) {
    let digits = String(value ?? '').replace(/[^\d\u09E6-\u09EF\u0660-\u0669]/g, '');
    const ascii = toAsciiDigits(digits);
    if (ascii.startsWith('880') && ascii.length >= 13) {
        digits = digits.slice(3);
    }
    return digits.slice(0, 11);
}

export function isValidBdMobile(value) {
    const ascii = toAsciiDigits(normalizeBdMobile(value));
    return /^01[3-9]\d{8}$/.test(ascii);
}

const FIELD_HISTORY_KEY = 'fuel-card-form-field-history';
const FORM_SNAPSHOT_KEY = 'fuel-card-form-last-snapshot';
const FIELD_HISTORY_MAX = 8;

const REQUIRED_FIELDS = [
    'applicantName',
    'fatherOrProprietor',
    'nidOrLicense',
    'mobile',
    'villageRoad',
    'union',
    'postOffice',
    'thanaUpazila',
    'district',
    'pumpName',
    'machineName',
    'fuelLiters',
    'purpose',
];

export function getFuelCardFieldHistory(fieldName) {
    if (typeof window === 'undefined' || !fieldName) return [];
    try {
        const store = JSON.parse(localStorage.getItem(FIELD_HISTORY_KEY) || '{}');
        const list = store[fieldName];
        return Array.isArray(list) ? list.filter((v) => typeof v === 'string' && v.trim()) : [];
    } catch {
        return [];
    }
}

export function getFuelCardFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...INITIAL_FUEL_CARD_FORM, ...parsed };
    } catch {
        return null;
    }
}

export function saveFuelCardFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {
            applicantName: displayField(form.applicantName),
            fatherOrProprietor: displayField(form.fatherOrProprietor),
            nidOrLicense: displayField(form.nidOrLicense),
            mobile: displayField(form.mobile),
            villageRoad: displayField(form.villageRoad),
            union: displayField(form.union),
            postOffice: displayField(form.postOffice),
            thanaUpazila: displayField(form.thanaUpazila),
            district: displayField(form.district),
            pumpName: displayField(form.pumpName),
            machineName: displayField(form.machineName),
            fuelType: form.fuelType || INITIAL_FUEL_CARD_FORM.fuelType,
            fuelLiters: displayField(form.fuelLiters),
            purpose: displayField(form.purpose),
        };
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

export function getFuelCardFieldSuggestions(fieldName) {
    const history = getFuelCardFieldHistory(fieldName);
    const snapshot = getFuelCardFormSnapshot();
    const fromSnapshot = snapshot ? displayField(snapshot[fieldName]) : '';
    return [...new Set([...history, fromSnapshot].filter(Boolean))];
}

export function saveFuelCardFieldHistory(fieldName, value) {
    if (typeof window === 'undefined' || !fieldName) return;
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return;

    try {
        const store = JSON.parse(localStorage.getItem(FIELD_HISTORY_KEY) || '{}');
        const prev = Array.isArray(store[fieldName]) ? store[fieldName] : [];
        const next = [trimmed, ...prev.filter((v) => v !== trimmed)].slice(0, FIELD_HISTORY_MAX);
        store[fieldName] = next;
        localStorage.setItem(FIELD_HISTORY_KEY, JSON.stringify(store));
    } catch {
        /* ignore */
    }
}

export function getMobileValidationError(value, t) {
    const digits = normalizeBdMobile(value);
    if (!digits) {
        return '';
    }
    if (digits.length < 11) {
        return t('fuelCardForm.validation.mobileLength', { count: digits.length });
    }
    if (!isValidBdMobile(digits)) {
        return t('fuelCardForm.validation.mobileInvalid');
    }
    return '';
}

/**
 * @param {typeof INITIAL_FUEL_CARD_FORM} form
 * @param {(key: string, opts?: object) => string} t
 */
export function getFuelCardFormValidationErrors(form, t) {
    const errors = [];

    for (const key of REQUIRED_FIELDS) {
        if (key === 'mobile') continue;
        if (!displayField(form[key])) {
            errors.push({
                field: key,
                message: t('fuelCardForm.validation.required', {
                    field: t(`fuelCardForm.fields.${key}`),
                }),
            });
        }
    }

    if (!form.fuelType) {
        errors.push({
            field: 'fuelType',
            message: t('fuelCardForm.validation.fuelTypeRequired'),
        });
    }

    const mobile = displayField(form.mobile);
    if (!mobile) {
        errors.push({ field: 'mobile', message: t('fuelCardForm.validation.mobileRequired') });
    } else {
        const mobileErr = getMobileValidationError(form.mobile, t);
        if (mobileErr) {
            errors.push({ field: 'mobile', message: mobileErr });
        }
    }

    return errors;
}

export function isFuelCardFormComplete(form, t) {
    return getFuelCardFormValidationErrors(form, t).length === 0;
}

/**
 * Every blank still empty, named the way the form asks for it and in the order
 * it asks — what the download confirmation lists back to the applicant. The fuel
 * type is a choice that always holds a value, so it is never listed.
 *
 * @param {typeof INITIAL_FUEL_CARD_FORM} form
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export function getEmptyFuelCardFields(form, t) {
    return Object.keys(INITIAL_FUEL_CARD_FORM)
        .filter((key) => key !== 'fuelType' && !displayField(form?.[key]))
        .map((key) => t(`fuelCardForm.fields.${key}`));
}

export function buildFuelCardLetter(form, fuelTypeLabel) {
    return {
        applicantName: displayField(form.applicantName),
        fatherOrProprietor: displayField(form.fatherOrProprietor),
        villageRoad: displayField(form.villageRoad),
        union: displayField(form.union),
        postOffice: displayField(form.postOffice),
        thanaUpazila: displayField(form.thanaUpazila),
        district: displayField(form.district),
        nidOrLicense: displayField(form.nidOrLicense),
        pumpName: displayField(form.pumpName),
        machineName: displayField(form.machineName),
        fuelType: fuelTypeLabel,
        fuelLiters: displayField(form.fuelLiters),
        purpose: displayField(form.purpose),
        mobile: displayField(form.mobile),
    };
}

export function validationErrorsToFieldMap(errors) {
    const map = {};
    for (const { field, message } of errors) {
        map[field] = message;
    }
    return map;
}
